'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import {
  MARCHA_FORMATOS,
  abrirEnImportador,
  eliminarMarcha,
  formatearBytes,
  formatearDuracion,
  getImportadorInfo,
  getMarchasDeBanda,
  procesarMarchaArchivo,
  solicitarMarcha,
} from '@/lib/actions';
import { MARCHA_CATEGORIAS, type Banda, type MarchaCategoria, type MarchaDoc, type MarchaProcesada } from '@/lib/types';
import { SesionesEditor } from '@/components/jartimarchas/SesionesEditor';
import { RecorteMarcha, msATexto } from '@/components/jartimarchas/RecorteMarcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Check,
  Clock,
  Disc3,
  Download,
  ExternalLink,
  FileAudio,
  Loader2,
  Music,
  Scissors,
  Trash2,
  Upload,
  X,
  Youtube,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/** «0:12 → 3:40» si la marcha lleva recorte para el juego, o `null` si suena entera. */
function textoRecorte(m: MarchaDoc): string | null {
  const i = Number(m.recorteIniMs) || 0;
  const f = Number(m.recorteFinMs) || 0;
  if (!i && !f) return null;
  return `${msATexto(i)} → ${f > 0 ? msATexto(f) : 'final'}`;
}

/**
 * JARTIMARCHAS — la página de los directores de banda.
 *
 * A la izquierda, el repertorio de la banda (el que hasta ahora era solo texto en
 * la ficha). A la derecha, subir el audio de una de esas marchas: archivo o enlace
 * de YouTube, escucharla antes de mandarla, y solicitar el alta.
 *
 * Lo que se sube va a `pendiente` y lo aprueba un admin. En cuanto lo aprueba, el
 * servidor del mod lo ve en 15 s y se lo empuja a todos los jugadores: nadie tiene
 * que reconectar para oírla.
 */
export default function JartiMarchasPage() {
  const { user, userData, jartinetData } = useAuth();
  const { toast } = useToast();

  const esAdmin = userData?.role === 'admin';

  /** Bandas que puedes gestionar: las que diriges (todas si eres admin). */
  const misBandas = useMemo<Banda[]>(() => {
    const todas = jartinetData?.bandas || [];
    if (esAdmin) return [...todas].sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto, 'es'));
    if (!user) return [];
    return todas
      .filter((b) => Array.isArray(b.directores) && b.directores.includes(user.uid))
      .sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto, 'es'));
  }, [jartinetData, user, esAdmin]);

  const [bandaId, setBandaId] = useState<string>('');
  const [marchas, setMarchas] = useState<MarchaDoc[]>([]);
  const [cargando, setCargando] = useState(false);
  const [aBorrar, setABorrar] = useState<MarchaDoc | null>(null);
  /** Marcha abierta en el reproductor grande, para elegir su recorte. */
  const [aRecortar, setARecortar] = useState<MarchaDoc | null>(null);

  const banda = useMemo(() => misBandas.find((b) => b.id === bandaId) || null, [misBandas, bandaId]);

  useEffect(() => {
    if (!bandaId && misBandas.length > 0) setBandaId(misBandas[0].id);
  }, [misBandas, bandaId]);

  const recargar = useCallback(async () => {
    if (!bandaId) return;
    setCargando(true);
    setMarchas(await getMarchasDeBanda(bandaId));
    setCargando(false);
  }, [bandaId]);

  useEffect(() => { recargar(); }, [recargar]);

  /** Estado del audio para cada entrada del repertorio. */
  const porItem = useMemo(() => {
    const m = new Map<string, MarchaDoc>();
    for (const x of marchas) {
      // Solo las MARCHAS ocupan una entrada del repertorio. Tambor y random van con
      // nombre libre y tienen su propio bloque.
      if ((x.categoria || 'marcha') !== 'marcha') continue;
      // Si hay varias, manda la aceptada; si no, la pendiente.
      const prev = m.get(x.repertorioItem);
      if (!prev || (prev.estado !== 'aceptada' && x.estado === 'aceptada')) m.set(x.repertorioItem, x);
    }
    return m;
  }, [marchas]);

  const repertorio = useMemo(
    () => (Array.isArray(banda?.repertorio) ? banda!.repertorio : []).filter(Boolean),
    [banda],
  );

  const borrar = async () => {
    if (!aBorrar) return;
    const res = await eliminarMarcha(aBorrar.id);
    toast({
      title: res.success ? 'Hecho' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    setABorrar(null);
    recargar();
  };

  if (misBandas.length === 0) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center">
        <Music className="mx-auto mb-4 h-12 w-12 text-muted-foreground/40" />
        <h1 className="font-headline text-2xl font-bold">JartiMarchas</h1>
        <p className="mt-3 text-muted-foreground">
          Esta página es para los directores de banda. Si diriges una y no la ves aquí, pide que te
          añadan como director en la ficha de la banda.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Cabecera ── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-gradient-to-br from-primary/15 via-card/40 to-transparent p-6">
        <div className="absolute -top-16 -right-10 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-primary/30 bg-primary/10 p-2">
              <Music className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="font-headline text-2xl font-bold">JartiMarchas</h1>
              <p className="text-sm text-muted-foreground">
                Sube el audio de tu repertorio y suénalo dentro de Minecraft.
              </p>
            </div>
          </div>

          {/* Selector de banda */}
          <div className="mt-5 flex flex-wrap gap-2">
            {misBandas.map((b) => (
              <button
                key={b.id}
                onClick={() => setBandaId(b.id)}
                className={cn(
                  'rounded-xl border px-3 py-2 text-sm transition-all',
                  b.id === bandaId
                    ? 'border-primary/40 bg-primary/15 font-semibold text-primary'
                    : 'border-white/8 bg-card/40 text-muted-foreground hover:text-foreground',
                )}
              >
                {b.nombreCompleto}
              </button>
            ))}
          </div>
        </div>
      </div>

      {banda && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {/* ── IZQUIERDA: repertorio actual ── */}
          <section className="rounded-2xl border border-white/8 bg-card/40 p-5">
            <header className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-headline text-lg font-bold">Repertorio</h2>
                <p className="text-xs text-muted-foreground">
                  El de la ficha de la banda. Marca verde = ya tiene audio en el juego.
                </p>
              </div>
              {cargando && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </header>

            {repertorio.length === 0 ? (
              <p className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
                Esta banda no tiene repertorio escrito. Añádelo en la ficha de la banda
                (Bandas → tu banda → Repertorio) y aparecerá aquí.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {repertorio.map((item) => {
                  const m = porItem.get(item);
                  return (
                    <li
                      key={item}
                      className="flex items-center gap-3 rounded-xl bg-muted/40 px-3 py-2.5"
                    >
                      <EstadoPunto estado={m?.estado} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{item}</p>
                        {m && (
                          <p className="truncate text-[0.7rem] text-muted-foreground">
                            {m.nombre} · {formatearDuracion(m.duracionMs)} · {formatearBytes(m.bytes)}
                            {m.origen === 'youtube' && ' · YouTube'}
                            {textoRecorte(m) && <span className="text-primary"> · {textoRecorte(m)}</span>}
                            {m.estado === 'rechazada' && m.motivoRechazo && ` · ${m.motivoRechazo}`}
                          </p>
                        )}
                      </div>
                      {m && (
                        <div className="flex shrink-0 items-center gap-1.5">
                          <audio src={m.audioUrl} controls preload="none" className="h-8 w-40" />
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Elegir desde y hasta qué segundo suena en el juego"
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            onClick={() => setARecortar(m)}
                          >
                            <Scissors className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setABorrar(m)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Tambor y Random no salen en el repertorio escrito, así que van en su
                propio bloque: si no, se subirían y no se verían en ningún sitio. */}
            {(['tambor', 'random'] as const).map((cat) => {
              const items = marchas.filter((m) => m.categoria === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mt-5">
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-primary/60">
                    {cat === 'tambor' ? 'Tambor' : 'Random'}
                  </h3>
                  <ul className="space-y-1.5">
                    {items.map((m) => (
                      <li key={m.id} className="flex items-center gap-3 rounded-xl bg-muted/40 px-3 py-2.5">
                        <EstadoPunto estado={m.estado} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{m.nombre}</p>
                          <p className="truncate text-[0.7rem] text-muted-foreground">
                            {formatearDuracion(m.duracionMs)} · {formatearBytes(m.bytes)}
                            {textoRecorte(m) && <span className="text-primary"> · {textoRecorte(m)}</span>}
                            {m.estado === 'rechazada' && m.motivoRechazo && ` · ${m.motivoRechazo}`}
                          </p>
                        </div>
                        <audio src={m.audioUrl} controls preload="none" className="h-8 w-40 shrink-0" />
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Elegir desde y hasta qué segundo suena en el juego"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-primary"
                          onClick={() => setARecortar(m)}
                        >
                          <Scissors className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setABorrar(m)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}

            <div className="mt-4 flex flex-wrap gap-4 text-[0.7rem] text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> En el juego</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Pendiente</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> Rechazada</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-white/20" /> Sin audio</span>
            </div>
          </section>

          {/* ── DERECHA: subir ── */}
          <SubirMarcha
            bandaId={banda.id}
            repertorio={repertorio}
            ocupadas={new Set([...porItem.entries()].filter(([, m]) => m.estado !== 'rechazada').map(([k]) => k))}
            onHecho={recargar}
          />
        </div>
      )}

      {/* Sesiones: el guion de una procesión. Va debajo, a lo ancho, porque el
          editor necesita sitio (guion a la izquierda, audios a la derecha). */}
      {banda && <SesionesEditor bandaId={banda.id} marchas={marchas} />}

      {/* Reproductor grande: desde y hasta qué segundo suena en el juego. */}
      <RecorteMarcha marcha={aRecortar} onClose={() => setARecortar(null)} onGuardado={recargar} />

      <AlertDialog open={!!aBorrar} onOpenChange={(o) => !o && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar «{aBorrar?.nombre}»?</AlertDialogTitle>
            <AlertDialogDescription>
              Desaparecerá del repertorio dentro del juego. Los jugadores que ya la tengan
              descargada dejarán de verla en la lista.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar}>Quitar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Pestaña de YouTube: pegas el enlace y se abre el Importador con él dentro.
 *
 * **Cómo se sabe si el programa está instalado:** no se puede preguntar. Los
 * navegadores no dicen qué protocolos hay registrados (sería una huella digital
 * del equipo). Lo que se hace —igual que Zoom, Discord o Steam— es intentar
 * abrirlo y mirar si la pestaña PIERDE EL FOCO: si el sistema lanza el programa,
 * el navegador pasa a segundo plano. Si en dos segundos y medio no ha pasado
 * nada, se asume que no está y se ofrece la descarga.
 *
 * Es una heurística, así que el enlace para descargarlo queda siempre accesible.
 */
function LanzarImportador({ bandaId }: { bandaId: string }) {
  const [url, setUrl] = useState('');
  const [lanzando, setLanzando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [noInstalado, setNoInstalado] = useState(false);
  const [error, setError] = useState('');

  const valido = /^https?:\/\/(www\.|m\.|music\.)?(youtube\.com|youtu\.be|soundcloud\.com)\//i
    .test(url.trim());

  const lanzar = async () => {
    setError('');
    setAbierto(false);
    setNoInstalado(false);
    setLanzando(true);

    const res = await abrirEnImportador(bandaId, url.trim());
    if (!res.success || !res.url) {
      setLanzando(false);
      setError(res.message || 'No se pudo preparar la sesión.');
      return;
    }

    // Si el programa arranca, el navegador pierde el foco. Es la única señal que
    // da el sistema; no hay forma de preguntarlo directamente.
    let saltó = false;
    const marcar = () => { saltó = true; };
    window.addEventListener('blur', marcar);
    document.addEventListener('visibilitychange', marcar);

    window.location.href = res.url;

    setTimeout(() => {
      window.removeEventListener('blur', marcar);
      document.removeEventListener('visibilitychange', marcar);
      setLanzando(false);
      if (saltó || document.hidden) setAbierto(true);
      else setNoInstalado(true);
    }, 2500);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="yt" className="text-xs">
          Enlace de YouTube o SoundCloud — un audio suelto o una lista entera
        </Label>
        <Input
          id="yt"
          className="mt-2"
          value={url}
          onChange={(e) => { setUrl(e.target.value); setAbierto(false); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && valido && !lanzando) lanzar(); }}
          placeholder="youtube.com/watch?v=…  ·  …/playlist?list=…  ·  soundcloud.com/…"
        />
        <p className="mt-2 text-[0.7rem] leading-relaxed text-muted-foreground">
          Se abrirá el Importador con el enlace ya puesto. Eliges las marchas, y al
          descargarlas <b>se suben aquí solas</b>.
        </p>
      </div>

      <Button onClick={lanzar} disabled={!valido || lanzando} className="w-full">
        {lanzando
          ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Abriendo el Importador…</>)
          : (<><ExternalLink className="mr-2 h-4 w-4" /> Abrir en el Importador</>)}
      </Button>

      {abierto && (
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3">
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <Check className="h-4 w-4" /> Importador abierto — sigue allí
          </div>
          <button
            onClick={() => { setAbierto(false); setNoInstalado(true); }}
            className="mt-1 text-[0.7rem] text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            ¿No se ha abierto nada?
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {noInstalado && <ImportadorCard />}

      {!abierto && !noInstalado && (
        <button
          onClick={() => setNoInstalado(true)}
          className="w-full text-center text-[0.7rem] text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Todavía no tengo el Importador
        </button>
      )}
    </div>
  );
}

/**
 * Tarjeta de descarga del Importador.
 *
 * El tamaño y la fecha salen del propio fichero de Storage, así que publicar una
 * versión nueva del programa no obliga a tocar esta página.
 */
function ImportadorCard() {
  const [info, setInfo] = useState<{ url: string; bytes: number; actualizado: string } | null>(null);

  useEffect(() => { getImportadorInfo().then(setInfo); }, []);

  return (
    <div className="rounded-xl border border-primary/25 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-lg border border-primary/30 bg-primary/10 p-2">
          <Disc3 className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold leading-tight">Importador de JartiMarchas</h3>
          <p className="mt-1 text-[0.7rem] leading-relaxed text-muted-foreground">
            YouTube no deja descargar desde nuestros servidores, pero desde tu ordenador
            sí. Este programa lo hace por ti: pegas un enlace —o una <b>playlist entera</b>—
            y te deja los ficheros listos para subirlos aquí.
          </p>
        </div>
      </div>

      <ol className="mt-3 space-y-1 pl-1 text-[0.7rem] text-muted-foreground">
        <li><b className="text-foreground/80">1.</b> Descarga y abre el programa.</li>
        <li><b className="text-foreground/80">2.</b> Pega el enlace y descarga las marchas.</li>
        <li><b className="text-foreground/80">3.</b> Súbelas aquí por la pestaña <b>Archivo</b>.</li>
      </ol>

      <a href={info?.url || '#'} download className={cn('mt-3 block', !info && 'pointer-events-none opacity-60')}>
        <Button className="w-full" disabled={!info}>
          <Download className="mr-2 h-4 w-4" />
          {info ? 'Descargar el Importador' : 'Cargando…'}
        </Button>
      </a>

      <p className="mt-2 text-center text-[0.65rem] text-muted-foreground">
        {info
          ? <>Windows · {formatearBytes(info.bytes)}
              {info.actualizado && ` · ${new Date(info.actualizado).toLocaleDateString('es-ES')}`}
            </>
          : 'Windows'}
        {' · '}
        <span title="El programa no está firmado digitalmente">
          Windows avisará: «Más información → Ejecutar de todas formas»
        </span>
      </p>
    </div>
  );
}

function EstadoPunto({ estado }: { estado?: string }) {
  const color =
    estado === 'aceptada' ? 'bg-emerald-500'
      : estado === 'pendiente' ? 'bg-amber-500'
        : estado === 'rechazada' ? 'bg-red-500'
          : 'bg-white/20';
  return <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', color)} />;
}

// ────────────────────────────────────────────────────────────────────────────
// Panel de subida
// ────────────────────────────────────────────────────────────────────────────

function SubirMarcha({
  bandaId,
  repertorio,
  ocupadas,
  onHecho,
}: {
  bandaId: string;
  repertorio: string[];
  ocupadas: Set<string>;
  onHecho: () => void;
}) {
  const { toast } = useToast();
  const [modo, setModo] = useState<'archivo' | 'youtube'>('archivo');
  const [file, setFile] = useState<File | null>(null);
  const [nombre, setNombre] = useState('');
  const [item, setItem] = useState('');
  const [categoria, setCategoria] = useState<MarchaCategoria>('marcha');
  const [procesando, setProcesando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState<MarchaProcesada | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const libres = useMemo(() => repertorio.filter((r) => !ocupadas.has(r)), [repertorio, ocupadas]);

  useEffect(() => {
    // Si la entrada elegida deja de estar libre (otro director se adelantó), se suelta.
    if (item && !libres.includes(item)) setItem('');
  }, [libres, item]);

  const limpiar = () => {
    setFile(null);
    setNombre('');
    setItem('');
    setListo(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  /**
   * Solo para la pestaña de ARCHIVO. Lo de YouTube ya no pasa por aquí: lo hace el
   * Importador en el ordenador del director, porque YouTube bloquea las descargas
   * desde los servidores.
   */
  const procesar = async () => {
    if (!file) return;
    setError('');
    setListo(null);
    setProcesando(true);
    const res = await procesarMarchaArchivo(bandaId, file);
    setProcesando(false);

    if (!res.success || !res.data) {
      setError(res.message || 'No se pudo procesar.');
      return;
    }
    setListo(res.data);
    if (!nombre && res.data.tituloSugerido) setNombre(res.data.tituloSugerido.slice(0, 80));
  };

  const enviar = async () => {
    if (!listo) return;
    setEnviando(true);
    const res = await solicitarMarcha({
      bandaId,
      nombre: nombre.trim(),
      categoria,
      // Tambor y random no se atan al repertorio: su referencia es su nombre.
      repertorioItem: categoria === 'marcha' ? item : nombre.trim(),
      audioUrl: listo.audioUrl,
      hash: listo.hash,
      bytes: listo.bytes,
      duracionMs: listo.duracionMs,
      origen: listo.origen,
      sourceUrl: listo.sourceUrl,
    });
    setEnviando(false);
    toast({
      title: res.success ? 'Enviado' : 'Error',
      description: res.message,
      variant: res.success ? undefined : 'destructive',
    });
    if (res.success) {
      limpiar();
      onHecho();
    }
  };

  const puedeProcesar = !!file;
  const puedeEnviar = !!listo && !!nombre.trim() && (categoria !== 'marcha' || !!item);

  return (
    <section className="rounded-2xl border border-white/8 bg-card/40 p-5">
      <header className="mb-4">
        <h2 className="font-headline text-lg font-bold">Subir una marcha</h2>
        <p className="text-xs text-muted-foreground">
          La escuchas aquí antes de mandarla. Se convierte sola a mono y con el volumen
          igualado, que es lo que necesita el juego.
        </p>
      </header>

      {/* Origen */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        <button
          onClick={() => { setModo('archivo'); setListo(null); setError(''); }}
          className={cn(
            'flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-all',
            modo === 'archivo'
              ? 'border-primary/40 bg-primary/15 font-semibold text-primary'
              : 'border-white/8 bg-muted/30 text-muted-foreground hover:text-foreground',
          )}
        >
          <FileAudio className="h-4 w-4" /> Archivo
        </button>
        <button
          onClick={() => { setModo('youtube'); setListo(null); setError(''); }}
          className={cn(
            'flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-all',
            modo === 'youtube'
              ? 'border-primary/40 bg-primary/15 font-semibold text-primary'
              : 'border-white/8 bg-muted/30 text-muted-foreground hover:text-foreground',
          )}
        >
          <Youtube className="h-4 w-4" /> YouTube y SoundCloud
        </button>
      </div>

      {modo === 'archivo' ? (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept={MARCHA_FORMATOS}
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setListo(null); setError(''); }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex w-full items-center gap-3 rounded-xl border border-dashed border-white/15 bg-muted/20 px-4 py-5 text-left transition-colors hover:border-primary/40"
          >
            <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">
                {file ? file.name : 'Elegir archivo de audio'}
              </span>
              <span className="block text-[0.7rem] text-muted-foreground">
                {file ? formatearBytes(file.size) : 'mp3, wav, m4a, flac, ogg… hasta 60 MB'}
              </span>
            </span>
          </button>
        </div>
      ) : (
        <LanzarImportador bandaId={bandaId} />
      )}

      {modo === 'archivo' && (
        <Button
          onClick={procesar}
          disabled={!puedeProcesar || procesando}
          className="mt-4 w-full"
        >
          {procesando
            ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Procesando audio…</>)
            : 'Procesar y escuchar'}
        </Button>
      )}

      {error && modo === 'archivo' && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Vista previa + datos */}
      {listo && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 space-y-4 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-400">
            <Check className="h-4 w-4" /> Audio listo — escúchalo antes de enviarlo
          </div>
          <audio src={listo.audioUrl} controls className="w-full" />
          <p className="text-[0.7rem] text-muted-foreground">
            {formatearDuracion(listo.duracionMs)} · {formatearBytes(listo.bytes)} · OGG mono
          </p>

          <div className="space-y-2">
            <Label htmlFor="nom" className="text-xs">Nombre de la marcha</Label>
            <Input
              id="nom"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Amarguras"
              maxLength={80}
            />
          </div>

          {/* Qué es: una marcha del repertorio (que se ata a una entrada concreta)
              o un audio de Tambor / Random, que van con nombre libre. */}
          <div className="space-y-2">
            <Label className="text-xs">¿Qué es este audio?</Label>
            <div className="grid grid-cols-3 gap-2">
              {MARCHA_CATEGORIAS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setCategoria(c.id); if (c.id !== 'marcha') setItem(''); }}
                  className={cn(
                    'rounded-lg px-2 py-2 text-xs transition-colors',
                    categoria === c.id
                      ? 'bg-primary/20 font-semibold text-primary'
                      : 'bg-muted/30 text-muted-foreground hover:bg-muted/60',
                  )}
                >
                  {c.id === 'marcha' ? 'Marcha' : c.plural}
                </button>
              ))}
            </div>
          </div>

          {categoria === 'marcha' && (
            <div className="space-y-2">
              <Label className="text-xs">Asignar a una marcha del repertorio</Label>
              {libres.length === 0 ? (
                <p className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
                  Todas las marchas del repertorio tienen ya audio. Añade más al repertorio en
                  la ficha de la banda, o súbelo como Tambor o Random.
                </p>
              ) : (
                <div className="max-h-40 space-y-1 overflow-y-auto pr-1">
                  {libres.map((r) => (
                    <button
                      key={r}
                      onClick={() => setItem(r)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                        item === r ? 'bg-primary/20 font-semibold text-primary' : 'bg-muted/30 hover:bg-muted/60',
                      )}
                    >
                      {item === r ? <Check className="h-3.5 w-3.5 shrink-0" /> : <span className="w-3.5" />}
                      <span className="truncate">{r}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={enviar} disabled={!puedeEnviar || enviando} className="flex-1">
              {enviando
                ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando…</>)
                : (<><Clock className="mr-2 h-4 w-4" /> Solicitar añadir al repertorio</>)}
            </Button>
            <Button variant="ghost" onClick={limpiar} className="shrink-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      )}
    </section>
  );
}
